/**
 * Client-side geocoding for the Direct Mail map.
 *
 * The Maps JS bundle is loaded once at app boot by unified-main.tsx, so nothing
 * here injects a script tag — callers wait for `window.google.maps` to appear.
 *
 * Reverse geocoding is serialized through a queue because a rep clicking down a
 * street fires requests far faster than Google's per-second budget allows;
 * firing them in parallel earns OVER_QUERY_LIMIT and silently loses houses.
 * The queue also collapses repeat clicks on the same rooftop into one request.
 */

export interface GeocodedAddress {
  formatted_address: string;
  address_line1: string;
  city: string;
  state: string;
  postal_code: string;
  place_id: string;
}

const READY_POLL_MS = 200;
const READY_TIMEOUT_MS = 15000;
const QUEUE_GAP_MS = 120;
const RATE_LIMIT_BACKOFF_MS = 700;
const MAX_ATTEMPTS = 3;

export const MAPS_UNAVAILABLE_MESSAGE =
  'Google Maps did not load. Set VITE_GOOGLE_MAPS_API_KEY for this environment and reload.';

export function isGoogleMapsReady(): boolean {
  return Boolean(window.google?.maps);
}

export function waitForGoogleMaps(timeoutMs: number = READY_TIMEOUT_MS): Promise<void> {
  if (isGoogleMapsReady()) return Promise.resolve();

  return new Promise((resolve, reject) => {
    const deadline = Date.now() + timeoutMs;
    const poll = () => {
      if (isGoogleMapsReady()) {
        resolve();
        return;
      }
      if (Date.now() >= deadline) {
        reject(new Error(MAPS_UNAVAILABLE_MESSAGE));
        return;
      }
      setTimeout(poll, READY_POLL_MS);
    };
    poll();
  });
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function component(
  result: google.maps.GeocoderResult,
  type: string,
  short = false
): string {
  const match = result.address_components.find((part) => part.types.includes(type));
  if (!match) return '';
  return short ? match.short_name : match.long_name;
}

function toAddress(result: google.maps.GeocoderResult): GeocodedAddress {
  const streetNumber = component(result, 'street_number');
  const route = component(result, 'route');
  return {
    formatted_address: result.formatted_address,
    address_line1: [streetNumber, route].filter(Boolean).join(' '),
    city:
      component(result, 'locality') ||
      component(result, 'sublocality') ||
      component(result, 'postal_town'),
    state: component(result, 'administrative_area_level_1', true),
    postal_code: component(result, 'postal_code'),
    place_id: result.place_id ?? '',
  };
}

/**
 * Google returns the tightest match first only most of the time; on satellite
 * clicks it often leads with a plus-code or a neighborhood, so prefer a result
 * that is actually a house.
 */
function bestResult(results: google.maps.GeocoderResult[]): google.maps.GeocoderResult {
  const ranked = ['street_address', 'premise', 'subpremise', 'route'];
  for (const type of ranked) {
    const match = results.find((result) => result.types.includes(type));
    if (match) return match;
  }
  return results[0];
}

function messageForStatus(status: string): string {
  switch (status) {
    case 'ZERO_RESULTS':
      return 'No street address at that point — try clicking closer to the house.';
    case 'OVER_QUERY_LIMIT':
      return 'Google is rate-limiting address lookups. Wait a moment and retry.';
    case 'REQUEST_DENIED':
      return 'Google rejected the address lookup — check the Maps API key and its enabled APIs.';
    case 'INVALID_REQUEST':
      return 'That location could not be looked up.';
    default:
      return `Address lookup failed (${status}).`;
  }
}

class GeocoderError extends Error {
  readonly status: string;

  constructor(status: string) {
    super(messageForStatus(status));
    this.name = 'GeocoderError';
    this.status = status;
  }
}

let sharedGeocoder: google.maps.Geocoder | null = null;

async function geocoder(): Promise<google.maps.Geocoder> {
  await waitForGoogleMaps();
  if (!sharedGeocoder) {
    sharedGeocoder = new window.google.maps.Geocoder();
  }
  return sharedGeocoder;
}

/**
 * The promise form of `geocode` is used rather than the callback form: the SDK
 * returns a promise either way, and ignoring it leaves an unhandled rejection
 * in the console every time a lookup fails.
 */
async function runGeocode(
  request: google.maps.GeocoderRequest
): Promise<google.maps.GeocoderResult[]> {
  const service = await geocoder();
  try {
    const response = await service.geocode(request);
    if (!response.results || response.results.length === 0) {
      throw new GeocoderError('ZERO_RESULTS');
    }
    return response.results;
  } catch (error) {
    if (error instanceof GeocoderError) throw error;
    const code = (error as { code?: unknown } | null)?.code;
    throw new GeocoderError(typeof code === 'string' ? code : 'UNKNOWN_ERROR');
  }
}

export interface ReverseGeocodeQueue {
  /** Resolves with the street address for a clicked point. Rejects with a readable message. */
  enqueue(lat: number, lng: number): Promise<GeocodedAddress>;
  /** Requests still waiting behind the one in flight. */
  pending(): number;
}

interface QueueJob {
  lat: number;
  lng: number;
  resolve: (address: GeocodedAddress) => void;
  reject: (error: unknown) => void;
}

export function createReverseGeocodeQueue(): ReverseGeocodeQueue {
  const jobs: QueueJob[] = [];
  const inFlight = new Map<string, Promise<GeocodedAddress>>();
  let draining = false;

  const cacheKey = (lat: number, lng: number) => `${lat.toFixed(6)},${lng.toFixed(6)}`;

  async function execute(job: QueueJob): Promise<GeocodedAddress> {
    for (let attempt = 1; ; attempt += 1) {
      try {
        const results = await runGeocode({ location: { lat: job.lat, lng: job.lng } });
        return toAddress(bestResult(results));
      } catch (error) {
        const rateLimited = error instanceof GeocoderError && error.status === 'OVER_QUERY_LIMIT';
        if (!rateLimited || attempt >= MAX_ATTEMPTS) throw error;
        await delay(RATE_LIMIT_BACKOFF_MS * attempt);
      }
    }
  }

  async function drain(): Promise<void> {
    if (draining) return;
    draining = true;
    try {
      while (jobs.length > 0) {
        const job = jobs.shift()!;
        try {
          job.resolve(await execute(job));
        } catch (error) {
          job.reject(error);
        }
        if (jobs.length > 0) await delay(QUEUE_GAP_MS);
      }
    } finally {
      draining = false;
    }
  }

  return {
    enqueue(lat, lng) {
      const key = cacheKey(lat, lng);
      const existing = inFlight.get(key);
      if (existing) return existing;

      const promise = new Promise<GeocodedAddress>((resolve, reject) => {
        jobs.push({ lat, lng, resolve, reject });
        void drain();
      }).finally(() => {
        inFlight.delete(key);
      });

      inFlight.set(key, promise);
      return promise;
    },
    pending() {
      return jobs.length;
    },
  };
}

/** One-shot address → coordinates, used to center the map on the org's office. */
export async function forwardGeocode(
  address: string
): Promise<{ lat: number; lng: number } | null> {
  try {
    const results = await runGeocode({ address });
    const location = results[0].geometry?.location;
    return location ? { lat: location.lat(), lng: location.lng() } : null;
  } catch {
    return null;
  }
}
